/**
 * LearnerProgressDetailModal Component
 *
 * 학습자 학습 상세 현황 모달
 * - 전체 진도율/완료 레슨/정답률 요약
 * - Course → Module → Lesson → Component 트리 뷰
 * - 각 레벨별 상태 배지 표시
 */

import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useQuery } from '@tanstack/react-query'
import {
    Loader2, AlertTriangle, ChevronDown, ChevronRight,
    Play, FileText, PenTool, CheckCircle2, Clock, MinusCircle
} from 'lucide-react'
import {
    reportApi,
    type LearnerProgressDetailModalData,
    type CourseProgressDetail,
    type ModuleProgressDetail,
    type LessonProgressDetail,
    type ComponentProgressDetail,
} from '@/api/b2b'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'

// ============================================================================
// Types
// ============================================================================

export interface LearnerProgressDetailModalProps {
    userId: number
    classProductId: number
    learnerName: string
    className: string
    isOpen: boolean
    onClose: () => void
}

// ============================================================================
// Helper Functions
// ============================================================================

function getLocationLabels(t: (key: string) => string): Record<string, string> {
    return {
        video: t('learnerDetail.location.video'),
        pre_quiz: t('learnerDetail.location.preQuiz'),
        post_quiz: t('learnerDetail.location.postQuiz'),
        wrapup_quiz: t('learnerDetail.location.wrapupQuiz'),
        checkup_quiz: t('learnerDetail.location.checkupQuiz'),
        assignment: t('learnerDetail.location.assignment'),
        module_start_quiz: t('learnerDetail.location.moduleStartQuiz'),
        module_end_quiz: t('learnerDetail.location.moduleEndQuiz'),
    }
}

function getStatusConfig(t: (key: string) => string): Record<string, { label: string; className: string }> {
    return {
        completed: { label: t('learnerDetail.status.completed'), className: 'bg-green-100 text-green-700' },
        in_progress: { label: t('learnerDetail.status.inProgress'), className: 'bg-blue-100 text-blue-700' },
        not_started: { label: t('learnerDetail.status.notStarted'), className: 'bg-gray-100 text-gray-500' },
    }
}

function getStatusBadge(status: string, t: (key: string) => string) {
    const configs = getStatusConfig(t)
    const config = configs[status] || configs.not_started
    return (
        <span className={cn('inline-flex px-2 py-0.5 text-xs font-medium rounded-full', config.className)}>
            {config.label}
        </span>
    )
}

function getStatusIcon(status: string) {
    switch (status) {
        case 'completed':
            return <CheckCircle2 className="w-4 h-4 text-green-500" />
        case 'in_progress':
            return <Clock className="w-4 h-4 text-blue-500" />
        default:
            return <MinusCircle className="w-4 h-4 text-gray-400" />
    }
}

function getComponentIcon(type: string) {
    switch (type) {
        case 'video':
            return <Play className="w-3.5 h-3.5 text-purple-500" />
        case 'quiz':
            return <FileText className="w-3.5 h-3.5 text-amber-500" />
        case 'assignment':
            return <PenTool className="w-3.5 h-3.5 text-teal-500" />
        default:
            return <FileText className="w-3.5 h-3.5 text-gray-400" />
    }
}

function formatWatchTime(seconds: number | null, t: (key: string, opts?: Record<string, unknown>) => string): string {
    if (seconds == null || seconds === 0) return '-'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    if (mins === 0) return t('learnerDetail.seconds', { count: secs })
    return t('learnerDetail.minutesSeconds', { minutes: mins, seconds: secs })
}

function formatDate(dateStr: string | null): string {
    if (!dateStr) return '-'
    try {
        return new Date(dateStr).toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        })
    } catch {
        return '-'
    }
}

// ============================================================================
// Component
// ============================================================================

export function LearnerProgressDetailModal({
    userId,
    classProductId,
    learnerName,
    className,
    isOpen,
    onClose,
}: LearnerProgressDetailModalProps) {
    const { t } = useTranslation('admin')
    const { data, isLoading, error } = useQuery({
        queryKey: ['learnerProgressDetailModal', userId, classProductId],
        queryFn: async () => {
            const res = await reportApi.getLearnerProgressDetailModal(userId, classProductId)
            return res.data?.data ?? null
        },
        enabled: isOpen && userId > 0 && classProductId > 0,
        staleTime: 60 * 1000,
    })

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
        <DialogContent className="sm:max-w-4xl">
            <DialogHeader>
                <DialogTitle>{t('learnerDetail.title', { name: learnerName, className })}</DialogTitle>
            </DialogHeader>
            {isLoading ? (
                <div className="flex justify-center items-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            ) : error || !data ? (
                <div className="py-20 text-center">
                    <AlertTriangle className="mx-auto mb-4 w-12 h-12 text-amber-500" />
                    <p className="text-gray-500">{t('learnerDetail.loadError')}</p>
                </div>
            ) : (
                <>
                    {/* Summary */}
                    <SummarySection summary={data.summary} progressPercent={data.progressPercent} />

                    {/* Tree View */}
                    <div className="mt-6">
                        <h3 className="mb-3 text-lg font-semibold text-gray-900">{t('learnerDetail.courseProgress')}</h3>
                        {data.courses.length === 0 ? (
                            <p className="py-8 text-sm text-center text-gray-500">{t('learnerDetail.noCourses')}</p>
                        ) : (
                            <div className="space-y-3">
                                {data.courses.map((course) => (
                                    <CourseAccordion key={course.contentId} course={course} />
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
            <DialogFooter>
                <button
                    onClick={onClose}
                    className="px-6 py-3 w-full font-semibold text-white rounded-xl transition-colors bg-primary-600 hover:bg-primary-700"
                >
                    {t('common.close')}
                </button>
            </DialogFooter>
        </DialogContent>
        </Dialog>
    )
}

// ============================================================================
// Sub Components
// ============================================================================

interface SummarySectionProps {
    summary: LearnerProgressDetailModalData['summary']
    progressPercent: number
}

function SummarySection({ summary, progressPercent }: SummarySectionProps) {
    const { t } = useTranslation('admin')
    return (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div className="p-4 bg-gray-50 rounded-xl">
                <p className="mb-1 text-xs text-gray-500">{t('learnerDetail.summary.overallProgress')}</p>
                <p className="text-2xl font-bold text-primary-600">{progressPercent.toFixed(0)}%</p>
                <div className="overflow-hidden mt-2 h-2 bg-gray-200 rounded-full">
                    <div
                        className="h-full rounded-full transition-all duration-500 bg-primary-500"
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-xl">
                <p className="mb-1 text-xs text-gray-500">{t('learnerDetail.summary.completedLessons')}</p>
                <p className="text-2xl font-bold text-gray-900">
                    {summary.completedLessons}
                    <span className="text-sm font-normal text-gray-500">/{summary.totalLessons}</span>
                </p>
            </div>

            <div className="p-4 bg-gray-50 rounded-xl">
                <p className="mb-1 text-xs text-gray-500">{t('learnerDetail.summary.lessonAccuracy')}</p>
                <p className="text-2xl font-bold text-gray-900">
                    {summary.lessonQuizAccuracy != null ? `${summary.lessonQuizAccuracy.toFixed(1)}%` : '-'}
                </p>
            </div>

            <div className="p-4 bg-gray-50 rounded-xl">
                <p className="mb-1 text-xs text-gray-500">{t('learnerDetail.summary.lastAccessed')}</p>
                <p className="text-lg font-bold text-gray-900">{formatDate(summary.lastAccessedAt)}</p>
            </div>
        </div>
    )
}

// Course Accordion
function CourseAccordion({ course }: { course: CourseProgressDetail }) {
    const { t } = useTranslation('admin')
    const [isOpen, setIsOpen] = useState(true)

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200">
            {/* Course Header */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex justify-between items-center px-4 py-3 w-full bg-white transition-colors hover:bg-gray-50"
            >
                <div className="flex gap-3 items-center">
                    {isOpen ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    {getStatusIcon(course.status)}
                    <span className="font-semibold text-gray-900"><ContentMetadataText text={course.title} /></span>
                    {getStatusBadge(course.status, t)}
                </div>
                <div className="flex gap-2 items-center">
                    <div className="overflow-hidden w-20 h-2 bg-gray-200 rounded-full">
                        <div
                            className="h-full rounded-full bg-primary-500"
                            style={{ width: `${course.progressPercent}%` }}
                        />
                    </div>
                    <span className="w-10 text-xs text-right text-gray-500">{course.progressPercent.toFixed(0)}%</span>
                </div>
            </button>

            {/* Course Body */}
            {isOpen && (
                <div className="border-t border-gray-100 bg-gray-50/50">
                    {course.modules.length === 0 ? (
                        <p className="py-4 text-sm text-center text-gray-500">{t('learnerDetail.noModules')}</p>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {course.modules.map((mod) => (
                                <ModuleAccordion key={mod.contentId} module={mod} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

// Module Accordion
function ModuleAccordion({ module }: { module: ModuleProgressDetail }) {
    const { t } = useTranslation('admin')
    const [isOpen, setIsOpen] = useState(module.status === 'in_progress')

    return (
        <div>
            {/* Module Header */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-6 py-2.5 hover:bg-gray-100/50 transition-colors"
            >
                <div className="flex items-center gap-2.5">
                    {isOpen ? (
                        <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                    ) : (
                        <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                    )}
                    {getStatusIcon(module.status)}
                    <span className="text-sm font-medium text-gray-800"><ContentMetadataText text={module.title} /></span>
                    {getStatusBadge(module.status, t)}
                    <span className="text-xs text-gray-400">
                        ({t('learnerDetail.completedOf', { completed: module.completedChildren, total: module.totalChildren })})
                    </span>
                </div>
                <div className="flex gap-2 items-center">
                    <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className="h-full rounded-full bg-primary-400"
                            style={{ width: `${module.progressPercent}%` }}
                        />
                    </div>
                    <span className="w-10 text-xs text-right text-gray-500">{module.progressPercent.toFixed(0)}%</span>
                </div>
            </button>

            {/* Module Body - Lessons */}
            {isOpen && (
                <div className="pr-4 pb-2 pl-12">
                    {module.lessons.length === 0 ? (
                        <p className="py-2 text-xs text-gray-400">{t('learnerDetail.noLessons')}</p>
                    ) : (
                        <div className="space-y-1.5">
                            {module.lessons.map((lesson) => (
                                <LessonRow key={lesson.contentId} lesson={lesson} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

// Lesson Row with inline components
function LessonRow({ lesson }: { lesson: LessonProgressDetail }) {
    const { t } = useTranslation('admin')
    return (
        <div className="p-3 bg-white rounded-lg border border-gray-100">
            <div className="flex gap-2 items-center mb-2">
                {getStatusIcon(lesson.status)}
                <span className="text-sm text-gray-800"><ContentMetadataText text={lesson.title} /></span>
                {getStatusBadge(lesson.status, t)}
            </div>

            {/* Components */}
            {lesson.components.length > 0 && (
                <div className="ml-6 space-y-1">
                    {lesson.components.map((comp, idx) => (
                        <ComponentRow key={`${comp.type}-${comp.location}-${idx}`} component={comp} />
                    ))}
                </div>
            )}
        </div>
    )
}

// Component Row
function ComponentRow({ component }: { component: ComponentProgressDetail }) {
    const { t } = useTranslation('admin')
    const labels = getLocationLabels(t)
    const configs = getStatusConfig(t)
    const locationLabel = labels[component.location] || component.location
    const statusCfg = configs[component.status] || configs.not_started

    return (
        <div className="flex justify-between items-center px-2 py-1 text-xs bg-gray-50 rounded">
            <div className="flex gap-2 items-center">
                {getComponentIcon(component.type)}
                <span className="text-gray-600">{locationLabel}</span>
                <span className={cn('px-1.5 py-0.5 rounded-full font-medium', statusCfg.className)}>
                    {statusCfg.label}
                </span>
            </div>
            <div className="flex gap-3 items-center text-gray-500">
                {component.quizScore != null && (
                    <span>
                        {t('learnerDetail.score')}: <span className="font-medium text-gray-700">{component.quizScore}{t('learnerDetail.points')}</span>
                        {component.quizPassed != null && (
                            <span className={cn('ml-1', component.quizPassed ? 'text-green-600' : 'text-red-500')}>
                                ({component.quizPassed ? t('learnerDetail.passed') : t('learnerDetail.failed')})
                            </span>
                        )}
                    </span>
                )}
                {component.videoWatchSeconds != null && component.videoWatchSeconds > 0 && (
                    <span>{t('learnerDetail.watched')}: <span className="font-medium text-gray-700">{formatWatchTime(component.videoWatchSeconds, t)}</span></span>
                )}
            </div>
        </div>
    )
}
